import { BindingEngine } from 'aurelia-binding';
import { NavDetailsViewModel, PortfolioReportClient } from '@dynamik/common/clients/reporting-clients';
import { CustomerService, IApplicationFilter, property, widget } from '@dynamik/common';
import { WidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';
import { format } from '../../../date-fns-local';

interface INavDetailsRow {
  text: string;
  accountCode?: number;
  groupName: string;
  balance: number;
  balanceEndOfLastYear: number;
  isGroup?: boolean;
  isAdjustment?: boolean;
  isTotal?: boolean;
  isGrandTotal?: boolean;
}

@customElement('nav-details-widget')
@widget('NAV details', true, true, 'Net asset value broken down per general ledger account, with the unbooked value adjustment per term')
export class NavDetailsWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, bindingEngine: BindingEngine,
    private portfolioReportClient: PortfolioReportClient) {
    super(element, bindingEngine, customerService);
  }

  /** Every row of the report, flattened so pages can be sliced out of it. */
  allRows: INavDetailsRow[];
  /** The rows on the page currently being displayed or printed. */
  rows: INavDetailsRow[];
  currentPage = 0;
  lastItemRendered: (value: unknown) => void;
  dateLabel: string;
  endOfLastYearLabel: string;

  @property('Data', 'Account number')
  showAccountCode: boolean = true;

  @property('Data', 'Group name')
  showGroupName: boolean = true;

  /** The end of last year balance is the opening balance of the year the report is run for. */
  @property('Data', 'Opening balance')
  showEndOfLastYear: boolean = true;

  @property('Table')
  rowsPerPage: number = 30;

  @property('Table')
  font?: string;

  @property('Table')
  fontSize?: number = 11;

  @property('Table')
  color?: string;

  // The endpoint derives the comparison date from the to date, so this is always the year being
  // reported on - opening balance at the end of last year, closing balance at the to date.
  protected override async loadData(filter: IApplicationFilter): Promise<NavDetailsViewModel> {
    return await this.portfolioReportClient.navDetails(filter.customer.companyId, filter.dates.toStr);
  }

  protected override async setData(data: NavDetailsViewModel): Promise<void> {
    this.dateLabel = format(data.date, 'dd.MM.yyyy');
    this.endOfLastYearLabel = format(data.endOfLastYearDate, 'dd.MM.yyyy');

    this.allRows = data.buckets.flatMap(bucket => [
      { text: bucket.bucket, groupName: '', balance: null, balanceEndOfLastYear: null, isGroup: true } as INavDetailsRow,
      ...bucket.accounts.map(account => ({
        text: account.isUnbookedValueAdjustment ? 'Ikke bokført verdijustering' : account.accountName,
        accountCode: account.isUnbookedValueAdjustment ? null : account.accountCode,
        groupName: account.groupName,
        balance: account.balance,
        balanceEndOfLastYear: account.balanceEndOfLastYear,
        isAdjustment: account.isUnbookedValueAdjustment
      } as INavDetailsRow)),
      {
        text: `${bucket.bucket} totalt`,
        groupName: '',
        balance: bucket.total,
        balanceEndOfLastYear: bucket.totalEndOfLastYear,
        isTotal: true
      } as INavDetailsRow
    ]);
    this.allRows.push({
      text: 'Verdijustert NAV',
      groupName: '',
      balance: data.valueAdjustedNetAssetValue,
      balanceEndOfLastYear: data.valueAdjustedNetAssetValueEndOfLastYear,
      isTotal: true,
      isGrandTotal: true
    });

    this.excelData = {
      fileName: 'NAV details',
      data: { Items: this.allRows }
    };
    this.updatePageData();
  }

  private updatePageData() {
    this.rows = this.allRows?.filter((x, i) => this.currentPage * this.rowsPerPage <= i && i < (this.currentPage + 1) * this.rowsPerPage);
  }

  getPageCount(): number {
    return this.allRows ? Math.ceil(this.allRows.length / this.rowsPerPage) : 0;
  }

  async setPage(page: number): Promise<void> {
    if (page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.updatePageData();
    await new Promise(resolve => this.lastItemRendered = resolve);
  }

  override configChanged(key: keyof NavDetailsWidget) {
    switch (key) {
      case 'rowsPerPage':
        this.updatePageData();
        break;
      default:
        break;
    }
  }
}
